/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Utility pro iCalendar Time API
 *
 * Obsahuje chybějící nebo vylepšené funkcionality z API od Mozilly.
 *
 * Zdroje:
 * - [iCal.js Time doc](https://mozilla-comm.github.io/ical.js/api/ICAL.Time.html)
 */
class IcalTimeUtil {
  /**
   * @param {IcalApi} ICAL
   */
  constructor(ICAL) {
    this.ICAL = ICAL;
  }

  /**
   * Posune iCal API reprezentaci data a času o zadaný časový interval.
   *
   * Jedná se o jednoduché, ale užitečné rozšížení originální metody
   * `ICAL.Time.adjust`.
   *
   * Prvním rozdílem je zavedení nepovinných položek na vstupu.
   * Originál vyžaduje vyplnění nul na nezměněných úrovních intervalu.
   *
   * Druhým rozdílem je imutabilita operace.
   *
   * Zdroje:
   * - [ICAL.Time.adjust](https://mozilla-comm.github.io/ical.js/api/ICAL.Time.html#adjust)
   * @param {IcalApi.Time} date
   * @param {Object} [opts={}]
   * @param {number} [opts.days]
   * @param {number} [opts.hours]
   * @param {number} [opts.minutes]
   * @param {number} [opts.seconds]
   * @returns {IcalApi.Time}
   */
  adjust(date, { days = 0, hours = 0, minutes = 0, seconds = 0 } = {}) {
    return date.clone().adjust(days, hours, minutes, seconds);
  }

  /**
   * Vytvoří iCal API reprezentaci data a času z UNIX času.
   *
   * Tato metoda nepochopitelně chybí v `ICAL.Time.fromUnixTime`.
   * Je přítomná pouze jako metoda u vytvořené instance.
   * U ostatních `from*` jsou statické metody přítomné.
   *
   * Zároveň byla přidána možnost volby cílové časové zóny, která u metody
   * v instanci chybí.
   * Další rozdíl oproti metodě v instanci je, že je imutabilní.
   *
   * Pro implementaci byl použit upravený kód metody z instance a to především
   * z důvodu výkonu, jinak by bylo nutné pro převod inicializovat 2 objekty.
   *
   * Zdroje:
   * - [ICAL.Time.fromUnixTime](https://mozilla-comm.github.io/ical.js/api/ICAL.Time.html#fromUnixTime)
   * - [ICAL.Time.toUnixTime](https://mozilla-comm.github.io/ical.js/api/ICAL.Time.html#toUnixTime)
   * @param {number} unixTime UNIX čas [s]
   * @param {Object} [opts={}]
   * @param {IcalApi.Timezone} [opts.zone] Časová zóna, do které se má UNIX čas
   * převést, výchozí je UTC zóna.
   * @returns {IcalApi.Time}
   */
  fromUnixTime(unixTime, { zone = this.ICAL.Timezone.utcTimezone } = {}) {
    const { ICAL } = this;
    const { Time, Timezone } = ICAL;
    // TODO Implementovat převod do libovolné časové zóny.
    // - Např. počátek UNIX epochy je o půlnoci 1970, ale v tom okamžiku bylo
    //   v ČR už jedna ráno.
    //   Při načtení UNIX času 0 se zónou CET by tedy měla být hodina nastavena
    //   na 1 a ne na 0, jako u UTC.
    // - Je nutné použít ICAL.Time.utcOffset pro výpočet odchylky času.
    //   Podrobnosti jak na to, lze najít ve zdrojovém kódě iCal API,
    //   např. v metodě toUnixTime.
    // - Níže uvedené časové zóny mají offset nulový, takže není třeba řešit.
    if (![Timezone.localTimezone, Timezone.utcTimezone].includes(zone)) {
      throw new Error('Only UTC and floating zones are implemented.');
    }
    // Využití imutabilní adjust metody pro posunutí času o požadovaní UNIX time
    // od počátečního data epochy (1970).
    // V originálním algoritmu se ještě používá Math.floor na sekundy,
    // ale pravděpodobně k tomu není žádný rozumný důvod.
    // V kódu se sekundy počítají ze zbytku po dělení a ten je vždy celočíselný.
    // Bez zbytečného zaokrouhlení se ušetří vytváření další instance.
    const time = this.adjust(Time.epochTime, { seconds: unixTime });
    // Optimalizace výkonu cachováním UNIX času.
    // Nelze provést jinak, než zásahem do privátní proměnné.
    // eslint-disable-next-line no-underscore-dangle
    time._cachedUnixTime = unixTime;
    // Nastavení časové zóny bez nové instance tak, jak ve `fromData`.
    time.zone = zone;
    return time;
  }

  /**
   * Vybere datum ze seznamu vstupu, které je v čase nejpozději ze všech.
   *
   * Zdroje:
   * - [ECMAScript Math.max](https://tc39.es/ecma262/#sec-math.max)
   * - [MDN Math.max](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/max)
   * - [Moment.js Maximum](https://momentjs.com/docs/#/get-set/max/)
   * @param {...IcalApi.Time} dates Neprázdný seznam datumů
   * @returns {IcalApi.Time}
   */
  max(...dates) {
    // TODO Vyžaduje alespoň jedno vyplněné datum.
    // - Není jasné, jakou hodnotu použít pro inicializaci.
    //   Dle ECMAScript má být -∞, což nemá v iCal API obdobu.
    //   Moment.js použivá aktuální datum, což moc smysl nedává.
    // - Zároveň je užitečné, aby byl výstupní typ jednotný.
    return dates.reduce((acc, date) => {
      if (date.toUnixTime() > acc.toUnixTime()) {
        return date;
      }
      return acc;
    });
  }

  /**
   * Vybere datum ze seznamu vstupu, které je v čase nejdříve ze všech.
   *
   * Zdroje:
   * - [ECMAScript Math.min](https://tc39.es/ecma262/#sec-math.min)
   * - [MDN Math.min](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/min)
   * - [Moment.js Minimum](https://momentjs.com/docs/#/get-set/min/)
   * @param {...IcalApi.Time} dates Neprázdný seznam datumů
   * @returns {IcalApi.Time}
   */
  min(...dates) {
    // TODO Vyžaduje alespoň jedno vyplněné datum.
    // - Není jasné, jakou hodnotu použít pro inicializaci.
    //   Dle ECMAScript má být ∞, což nemá v iCal API obdobu.
    //   Moment.js použivá aktuální datum, což moc smysl nedává.
    // - Zároveň je užitečné, aby byl výstupní typ jednotný.
    return dates.reduce((acc, date) => {
      if (date.toUnixTime() < acc.toUnixTime()) {
        return date;
      }
      return acc;
    });
  }
}

export default IcalTimeUtil;
